import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { endpoints } from "../../api/endpoints";
import type { User } from "../../types/user";

export function useProfile(userId: string) {
  return useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      const { data } = await api.get<User>(endpoints.account.me(userId));
      return data;
    },
    enabled: userId !== "",
  });
}

export function useUpdateProfile(userId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (patch: { firstName: string; lastName: string }) => {
      const current = client.getQueryData<User>(["profile", userId]);
      const { data } = await api.put<User>(endpoints.account.me(userId), {
        ...current,
        ...patch,
        // The detail endpoint repeats a role per authority; sending that back
        // multiplies the user's role rows.
        roles: [...new Set((current?.roles ?? []).map((role) => role.id))].map((id) => ({ id })),
      });
      return data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["profile", userId] }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: {
      oldPassword: string;
      newPassword: string;
      confirmPassword: string;
    }) => {
      const { data } = await api.post(endpoints.account.changePassword(), payload);
      return data;
    },
  });
}

export function useRequestEmailChange() {
  return useMutation({
    mutationFn: async (newEmail: string) => {
      const { data } = await api.post(endpoints.account.emailChangeOtp(), { otpFor: newEmail });
      return data;
    },
  });
}

export function useConfirmEmailChange() {
  return useMutation({
    mutationFn: async (payload: { email: string; otp: string }) => {
      const { data } = await api.put(endpoints.account.confirmEmailChange(), payload);
      return data;
    },
  });
}

export function useUploadAvatar(userId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(endpoints.users.profileImage(userId), form, {
        headers: { "Content-Type": undefined },
      });
      return data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["avatar", userId] }),
  });
}

export function useAvatar(userId: string) {
  return useQuery({
    queryKey: ["avatar", userId],
    queryFn: async () => {
      // 404 is the normal answer for a user who has never uploaded one.
      const { data } = await api.get<Blob>(endpoints.users.profileImage(userId), {
        responseType: "blob",
        silent: true,
      } as never);
      return URL.createObjectURL(data);
    },
    enabled: userId !== "",
    retry: false,
  });
}
